import { useState } from 'react';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import { useWalletSign } from './useWalletSign';
import { approveWithdrawal } from '../../services/xpxPerpsWithdrawalService';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE } from './constants';

type Props = {
  record: {
    id: string;
    userId: string;
    toAddress: string;
    amount: string;
    token: string;
    chainId: string;
  };
  onClose(): void;
};

export function ApproveDialog({ record, onClose }: Props) {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { detectedWallets, connect, address, signMessage } = useWalletSign();
  const [selectedWallet, setSelectedWallet] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!selectedWallet) return;
    await connect(selectedWallet.provider);
  };

  const handleSignAndApprove = async () => {
    if (!address) return;
    setLoading(true);
    try {
      const timestampMs = Date.now();
      const message = `Withdrawal ID: ${record.id}\nAction: Approve\nTimestamp: ${timestampMs}`;
      const signature = await signMessage(message);
      await approveWithdrawal(record.id, address, signature, timestampMs);
      notify(t('module.xpxPerpsWithdrawal.approveSuccess'), { type: 'success' });
      refresh();
      onClose();
    } catch (err: any) {
      notify(err?.response?.data?.message || err?.message || 'Error', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('module.xpxPerpsWithdrawal.approveTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2"><b>{t('module.xpxPerpsWithdrawal.userId')}:</b> {record.userId}</Typography>
          <Typography variant="body2"><b>{t('module.xpxPerpsWithdrawal.toAddress')}:</b> {record.toAddress}</Typography>
          <Typography variant="body2"><b>{t('module.xpxPerpsWithdrawal.amount')}:</b> {record.amount} {record.token}</Typography>
          <Typography variant="body2"><b>{t('module.xpxPerpsWithdrawal.chainId')}:</b> {record.chainId}</Typography>

          {detectedWallets.length === 0 ? (
            <Typography variant="body2" color="error">{t('module.xpxPerpsWithdrawal.noWalletFound')}</Typography>
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('module.xpxPerpsWithdrawal.selectWallet')}</InputLabel>
              <Select
                label={t('module.xpxPerpsWithdrawal.selectWallet')}
                value={selectedWallet?.name ?? ''}
                onChange={(e) => {
                  const w = detectedWallets.find((x) => x.name === e.target.value);
                  setSelectedWallet(w ?? null);
                }}
              >
                {detectedWallets.map((w) => (
                  <MenuItem key={w.name} value={w.name}>{w.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!address && selectedWallet && (
            <Button variant="outlined" size="small" onClick={handleConnect}>
              {t('module.xpxPerpsWithdrawal.connect')}
            </Button>
          )}

          {address && (
            <Typography variant="caption" color="text.secondary">
              {t('module.xpxPerpsWithdrawal.connectedAs')}: {address}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={loading}>
          {t('actions.cancel')}
        </Button>
        <PermissionGuard
          resource={XPX_PERPS_WITHDRAWAL_PERMISSION_RESOURCE}
          action="approve"
          fallback={<></>}
        loadingFallback={<></>}
        >
          <Button
            variant="contained"
            color="success"
            onClick={handleSignAndApprove}
            disabled={!address || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {t('module.xpxPerpsWithdrawal.signAndApprove')}
          </Button>
        </PermissionGuard>
      </DialogActions>
    </>
  );
}
